/**
 * Shared catalogue primitives.
 *
 * House style, deliberately: monospace for every label, chip, tag and numeral —
 * prose is the only thing set in a sans face. Hairline borders, no fills beyond
 * a faint tint, and no transitions at all. "Snappy" is a stated requirement, so
 * hover and focus states switch instantly rather than easing; that also means
 * there is nothing for prefers-reduced-motion to suppress.
 */
import React from 'react';
import { Dataset } from 'app/data/datasets';
import { NOT_DOCUMENTED, coverageOf } from './coverage';

/**
 * Muted, explicit gap marker. Never render an empty cell instead of this.
 *
 * Set in gray-400 (7.45:1 on #0d1117), not gray-500 (3.91:1) or gray-600
 * (2.50:1) — both fail WCAG AA for normal text. A gap marker is the one label
 * on this page that must stay readable, so "quieter than a real value" is
 * carried by the dashed underline, not by dimming it below the legibility floor.
 */
export function NotDocumented({ label }: { label?: string }) {
  return (
    <span
      className="font-mono text-xs italic text-gray-400 decoration-dotted underline underline-offset-2 decoration-gray-600"
      title={label ? `${label} has not been published yet` : undefined}
    >
      {NOT_DOCUMENTED}
    </span>
  );
}

type ChipTone = 'default' | 'beta' | 'available' | 'broad' | 'ticker';

const CHIP_TONES: Record<ChipTone, string> = {
  default: 'border-gray-700 text-gray-400',
  beta: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
  available: 'border-green-500/30 bg-green-500/10 text-green-400',
  broad: 'border-green-500/30 text-green-400',
  ticker: 'border-gray-800 bg-gray-900/80 text-gray-300',
};

export function Chip({
  children,
  tone = 'default',
  title,
}: {
  children: React.ReactNode;
  tone?: ChipTone;
  title?: string;
}) {
  return (
    <span
      title={title}
      className={`inline-flex items-center whitespace-nowrap rounded border px-1.5 py-0.5 font-mono text-[11px] leading-none ${CHIP_TONES[tone]}`}
    >
      {children}
    </span>
  );
}

export function StatusChip({ status }: { status: Dataset['status'] }) {
  return status === 'beta' ? (
    <Chip tone="beta" title="Live, but coverage figures are not yet fixed">
      beta
    </Chip>
  ) : (
    <Chip tone="available">available</Chip>
  );
}

/**
 * "n/m" — a count of published fields, deliberately not normalised into a
 * quality score. A score invites optimising the score; a count can only be
 * improved by publishing the missing field.
 */
export function CoverageMeter({ dataset }: { dataset: Dataset }) {
  const { documented, total, missing } = coverageOf(dataset);

  return (
    <span
      className="inline-flex items-center gap-1.5"
      title={
        missing.length
          ? `Not yet documented: ${missing.join(', ')}`
          : 'All coverage fields documented'
      }
    >
      {/* One tick per coverage field — reads at a glance, no arithmetic. */}
      <span aria-hidden="true" className="inline-flex gap-[2px]">
        {Array.from({ length: total }, (_, index) => (
          <span
            key={index}
            className={`h-2.5 w-[3px] ${
              index < documented ? 'bg-blue-400/80' : 'bg-gray-700'
            }`}
          />
        ))}
      </span>
      <span className="font-mono text-[11px] tabular-nums text-gray-400">
        {documented}/{total}
      </span>
    </span>
  );
}

/**
 * Real constituents observed in an unfiltered SDK pull — Databento's move of
 * showing you actual symbols rather than describing the universe in prose.
 *
 * Renders nothing at all when the registry has no `sampleTickers` for a
 * dataset. Most datasets were sampled with a ticker filter, so their "sample"
 * would only echo the filter back; an empty slot is honest, a fabricated one
 * is not. Never populate this from memory.
 */
export function ConstituentChips({
  tickers,
  limit = 6,
}: {
  tickers?: string[];
  limit?: number;
}) {
  if (!tickers || tickers.length === 0) return null;

  const shown = tickers.slice(0, limit);
  const rest = tickers.length - shown.length;

  return (
    <span
      className="inline-flex flex-wrap items-center gap-1"
      title={`Constituents observed in a live pull: ${tickers.join(', ')}`}
    >
      <span className="sr-only">Observed constituents: </span>
      {shown.map(ticker => (
        <span
          key={ticker}
          className={`inline-flex items-center whitespace-nowrap rounded-sm border px-1 py-px font-mono text-[10px] leading-none tabular-nums ${CHIP_TONES.ticker}`}
        >
          {ticker}
        </span>
      ))}
      {rest > 0 && (
        <span className="font-mono text-[10px] tabular-nums text-gray-400">
          +{rest}
        </span>
      )}
    </span>
  );
}
